import {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { Alert } from 'react-native';
import { useMutation } from '@apollo/client';

import { CREATE_MEDIA, MODIFY_MEDIA } from '../../../common/graphql/media';
import { useOfflineQueue } from '../../offline';
import { getPhotoId, isRemoteUrl } from './machineDirectionsHelpers';

// Visual direction photos of the machine's location and place: adding a captured photo
// once it has uploaded, removing photos and saving photo descriptions.
const useVisualDirectionPhotos = ({
	captureMedia,
	capturedMediaLink,
	hasValidPlace,
	machine,
	modifySelectedLocation,
	modifySelectedPlace,
	processingMedia,
}) => {
	const [handledCapturedMediaLink, setHandledCapturedMediaLink] = useState(null);
	const [pendingVisualDirectionOwner, setPendingVisualDirectionOwner] = useState(null);
	const { isOnline } = useOfflineQueue();
	const [createMedia] = useMutation(CREATE_MEDIA);
	const [modifyMedia] = useMutation(MODIFY_MEDIA);

	const locationVisualDirectionPhotos = useMemo(() => (
		machine.location?.visualDirectionPhotos || []
	), [machine.location?.visualDirectionPhotos]);
	const locationVisualDirectionPhotoUrls = useMemo(() => (
		(machine.location?.visualDirectionPhotoUrls || []).filter(isRemoteUrl)
	), [machine.location?.visualDirectionPhotoUrls]);
	const locationVisualDirectionPhotoIds = useMemo(() => (
		locationVisualDirectionPhotos.map(getPhotoId).filter(Boolean)
	), [locationVisualDirectionPhotos]);
	const placeVisualDirectionPhotos = useMemo(() => (
		machine.place?.visualDirectionPhotos || []
	), [machine.place?.visualDirectionPhotos]);
	const placeVisualDirectionPhotoUrls = useMemo(() => (
		(machine.place?.visualDirectionPhotoUrls || []).filter(isRemoteUrl)
	), [machine.place?.visualDirectionPhotoUrls]);
	const placeVisualDirectionPhotoIds = useMemo(() => (
		placeVisualDirectionPhotos.map(getPhotoId).filter(Boolean)
	), [placeVisualDirectionPhotos]);

	const addVisualDirectionPhoto = useCallback(ownerType => () => {
		if (!isOnline) {
			Alert.alert('Internet connection required', 'Visual direction photos need to upload before they can be saved.');
			return;
		}

		if (!processingMedia) {
			setPendingVisualDirectionOwner(ownerType);
			captureMedia();
		}
	}, [captureMedia, isOnline, processingMedia]);

	const removeLocationVisualDirectionPhoto = useCallback((photo) => {
		const photoId = getPhotoId(photo);
		Alert.alert(
			'Remove visual direction photo?',
			'',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Remove',
					style: 'destructive',
					onPress: () => modifySelectedLocation({
						visualDirectionPhotos: photo?.legacyUrl
							? locationVisualDirectionPhotoIds
							: locationVisualDirectionPhotoIds.filter(currentPhotoId => currentPhotoId !== photoId),
						visualDirectionPhotoUrls: locationVisualDirectionPhotoUrls.filter(currentPhotoUrl => currentPhotoUrl !== photo?.link),
					}),
				},
			],
		);
	}, [locationVisualDirectionPhotoIds, locationVisualDirectionPhotoUrls, modifySelectedLocation]);

	const removePlaceVisualDirectionPhoto = useCallback((photo) => {
		const photoId = getPhotoId(photo);
		Alert.alert(
			'Remove visual direction photo?',
			'',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Remove',
					style: 'destructive',
					onPress: () => modifySelectedPlace({
						visualDirectionPhotos: photo?.legacyUrl
							? placeVisualDirectionPhotoIds
							: placeVisualDirectionPhotoIds.filter(currentPhotoId => currentPhotoId !== photoId),
						visualDirectionPhotoUrls: placeVisualDirectionPhotoUrls.filter(currentPhotoUrl => currentPhotoUrl !== photo?.link),
					}),
				},
			],
		);
	}, [modifySelectedPlace, placeVisualDirectionPhotoIds, placeVisualDirectionPhotoUrls]);

	const updateVisualDirectionPhotoComment = useCallback(async (photo, comment) => {
		const photoId = getPhotoId(photo);
		if (!photoId || photo.legacyUrl) {
			return;
		}

		try {
			await modifyMedia({
				variables: {
					input: {
						id: photoId,
						comment: comment || null,
					},
				},
			});
		}
		catch (error) {
			Alert.alert('Description not saved', 'Unable to save the visual direction photo description.');
		}
	}, [modifyMedia]);

	useEffect(() => {
		if (!capturedMediaLink || !pendingVisualDirectionOwner) {
			return;
		}

		if (capturedMediaLink === handledCapturedMediaLink) {
			return;
		}

		setHandledCapturedMediaLink(capturedMediaLink);

		if (!isRemoteUrl(capturedMediaLink)) {
			Alert.alert('Photo not saved', 'Visual direction photos need an internet connection so the photo can upload before saving.');
			setPendingVisualDirectionOwner(null);
			return;
		}

		const isLocationOwner = pendingVisualDirectionOwner === 'location';
		const ownerId = isLocationOwner
			? machine.location?.id
			: (hasValidPlace ? machine.place?.id : null);
		const existingPhotos = isLocationOwner ? locationVisualDirectionPhotos : placeVisualDirectionPhotos;
		const existingPhotoUrls = isLocationOwner ? locationVisualDirectionPhotoUrls : placeVisualDirectionPhotoUrls;
		const existingPhotoIds = isLocationOwner ? locationVisualDirectionPhotoIds : placeVisualDirectionPhotoIds;
		const modifyOwner = isLocationOwner ? modifySelectedLocation : modifySelectedPlace;

		if (
			!ownerId ||
			existingPhotos.some(photo => photo?.link === capturedMediaLink) ||
			existingPhotoUrls.includes(capturedMediaLink)
		) {
			setPendingVisualDirectionOwner(null);
			return;
		}

		(async () => {
			try {
				const result = await createMedia({
					variables: {
						input: {
							[pendingVisualDirectionOwner]: ownerId,
							link: capturedMediaLink,
						},
					},
				});
				const mediaId = result?.data?.createMedia?.id;
				if (!mediaId) {
					Alert.alert('Photo not saved', 'Unable to save the visual direction photo.');
					return;
				}

				await modifyOwner({
					visualDirectionPhotos: [...existingPhotoIds, mediaId],
				});
			}
			catch (error) {
				Alert.alert('Photo not saved', 'Unable to save the visual direction photo.');
			}
			finally {
				setPendingVisualDirectionOwner(null);
			}
		})();
	}, [
		capturedMediaLink,
		createMedia,
		handledCapturedMediaLink,
		hasValidPlace,
		locationVisualDirectionPhotoIds,
		locationVisualDirectionPhotoUrls,
		locationVisualDirectionPhotos,
		machine.location?.id,
		machine.place?.id,
		modifySelectedLocation,
		modifySelectedPlace,
		pendingVisualDirectionOwner,
		placeVisualDirectionPhotoIds,
		placeVisualDirectionPhotos,
		placeVisualDirectionPhotoUrls,
	]);

	return {
		addVisualDirectionPhoto,
		locationVisualDirectionPhotoUrls,
		locationVisualDirectionPhotos,
		pendingVisualDirectionOwner,
		placeVisualDirectionPhotoUrls,
		placeVisualDirectionPhotos,
		removeLocationVisualDirectionPhoto,
		removePlaceVisualDirectionPhoto,
		updateVisualDirectionPhotoComment,
	};
};

export default useVisualDirectionPhotos;
